import { uploadFile } from 'dashboard/helper/uploadHelper';
import { waitForDelay } from 'dashboard/helper/delay';

/**
 * Check if a match position sits inside a <blockquote> in the HTML.
 * Counts open/close blockquote tags before the position.
 */
function isInsideBlockquote(html, position) {
  const before = html.substring(0, position);
  const openCount = (before.match(/<blockquote/gi) || []).length;
  const closeCount = (before.match(/<\/blockquote/gi) || []).length;
  return openCount > closeCount;
}

/**
 * Check if the HTML contains base64 images that need uploading,
 * i.e. images OUTSIDE of <blockquote> (quoted email content).
 * Images inside blockquotes are from previous emails and are left as-is.
 * @param {string} html
 * @returns {boolean}
 */
export function hasBase64Images(html) {
  if (!html || typeof html !== 'string') return false;
  const regex = /src=(["'])data:image\/[^"']+;base64,[\s\S]*?\1/gi;
  let match = regex.exec(html);
  while (match !== null) {
    if (!isInsideBlockquote(html, match.index)) {
      return true;
    }
    match = regex.exec(html);
  }
  return false;
}

/**
 * Convert a data-URI to a File object.
 * @param {string} dataUri  e.g. "data:image/png;base64,iVBOR..."
 * @param {string} filename e.g. "inline-image-0.png"
 * @returns {File}
 */
function base64ToFile(dataUri, filename) {
  const [header, base64Data] = dataUri.split(',');
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const normalizedBase64 = (base64Data || '').replace(/\s/g, '');

  const byteString = atob(normalizedBase64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    bytes[i] = byteString.charCodeAt(i);
  }

  return new File([bytes], filename, { type: mime });
}

// Le immagini incollate restano linkate nelle mail spedite: il server le salva
// sotto il prefisso pubblico `inline-images/<account>/`. Chi carica altro (es.
// la firma) passa il proprio `purpose` in uploadOptions.
const INLINE_IMAGE_UPLOAD_PURPOSE = 'inline_image';

const MAX_RETRIES = 1;
const BASE_DELAY_MS = 1000;
const BETWEEN_UPLOADS_MS = 300;
const INLINE_UPLOAD_TIMEOUT_MS = 20000;

function sleep(ms) {
  return waitForDelay(ms);
}

// Network errors (no response), 5xx and 429 are transient; other 4xx are not.
export function isRetryableUploadError(err) {
  const status = err?.response?.status;
  if (!status) return true;
  return status === 429 || status >= 500;
}

/**
 * Upload a single image with retry + exponential backoff.
 * On 429 waits progressively longer before retrying. Other 4xx (e.g. 422 for
 * a file that is not a supported image) are final: retrying cannot help.
 * @returns {Promise<{fileUrl: string|null, success: boolean}>}
 */
async function uploadWithRetry(m, index, accountId, uploadOptions = {}) {
  const filename = `inline-image-${index}.${m.extension}`;

  const attemptUpload = async (attempt) => {
    try {
      const file = base64ToFile(m.dataUri, filename);
      const result = await uploadFile(file, accountId, {
        purpose: INLINE_IMAGE_UPLOAD_PURPOSE,
        ...uploadOptions,
        timeoutMs: INLINE_UPLOAD_TIMEOUT_MS,
      });
      if (!result.fileUrl) {
        throw new Error('Inline image upload returned no file URL');
      }
      return { ...m, fileUrl: result.fileUrl, success: true };
    } catch (err) {
      if (attempt < MAX_RETRIES && isRetryableUploadError(err)) {
        const is429 = err?.response?.status === 429;
        const backoff = is429
          ? BASE_DELAY_MS * 2 ** (attempt + 1)
          : BASE_DELAY_MS * 2 ** attempt;
        await sleep(backoff);
        return attemptUpload(attempt + 1);
      }
      return { ...m, fileUrl: null, success: false };
    }
  };

  return attemptUpload(0);
}

/**
 * Find all base64-encoded <img> sources in the HTML, upload each via
 * the server-side upload endpoint, and replace the src with the returned
 * public URL.
 *
 * Key optimisation: base64 images inside <blockquote> (quoted email content
 * from previous messages) are left untouched — no upload, no stripping.
 * The recipient's email client renders them from the base64 data directly.
 *
 * Only images in the user's NEW content (outside blockquotes) are uploaded,
 * sequentially with exponential backoff to avoid 429 rate-limits.
 *
 * @param {string} html       The HTML content from the editor
 * @param {number} accountId
 * @param {object} uploadOptions Options forwarded to the shared upload helper
 * @returns {Promise<{html: string, uploadCount: number, errorCount: number}>}
 */
export async function extractBase64ImagesAndUpload(
  html,
  accountId,
  uploadOptions = {}
) {
  const regex = /src=(["'])(data:image\/([^;]+);base64,[\s\S]*?)\1/gi;
  const allMatches = [];
  let match = regex.exec(html);

  while (match !== null) {
    allMatches.push({
      fullMatch: match[0],
      quote: match[1],
      dataUri: match[2],
      extension: match[3],
      position: match.index,
    });
    match = regex.exec(html);
  }

  if (allMatches.length === 0) {
    return { html, uploadCount: 0, errorCount: 0 };
  }

  // Only upload images outside blockquotes (user's new content).
  // Images inside blockquotes are from previous emails — left as-is.
  const toUpload = allMatches.filter(
    (m) => !isInsideBlockquote(html, m.position)
  );

  if (toUpload.length === 0) {
    return { html, uploadCount: 0, errorCount: 0 };
  }

  let processedHtml = html;
  let uploadCount = 0;
  let errorCount = 0;

  // De-duplicate: identical dataURIs only need one upload
  const seen = new Map();
  await toUpload.reduce(
    (promise, m, i) =>
      promise.then(async () => {
        if (seen.has(m.dataUri)) {
          const cachedUrl = seen.get(m.dataUri);
          if (cachedUrl) {
            processedHtml = processedHtml
              .split(m.fullMatch)
              .join(`src=${m.quote}${cachedUrl}${m.quote}`);
            uploadCount += 1;
          } else {
            errorCount += 1;
          }
          return;
        }

        const result = await uploadWithRetry(m, i, accountId, uploadOptions);

        if (result.success) {
          processedHtml = processedHtml
            .split(m.fullMatch)
            .join(`src=${m.quote}${result.fileUrl}${m.quote}`);
          seen.set(m.dataUri, result.fileUrl);
          uploadCount += 1;
        } else {
          // Keep original base64 so the image still renders
          seen.set(m.dataUri, null);
          errorCount += 1;
        }

        // Small pause between uploads
        if (i < toUpload.length - 1) {
          await sleep(BETWEEN_UPLOADS_MS);
        }
      }),
    Promise.resolve()
  );

  return { html: processedHtml, uploadCount, errorCount };
}
